import React, { useCallback, useState, useRef } from 'react';
import { Box } from '@mui/material';
import TeamAssetsNavbar from 'team/components/adminAssets/TeamAssetsNavbar';
import TeamAssetsUploadCard from 'team/components/adminAssets/TeamAssetsUploadCard';
import TeamVirtualBackgroundModal from 'team/components/adminAssets/TeamVirtualBackgroundModal';
import { uploadImageBlob, deleteImage } from 'apis/imageStorageApi';
import { TEAM_ASSET_TYPES } from 'team/apis/teamAssetApi';
import { useTeamAssets } from 'team/hooks/useTeamAssets';

const TeamAdminAssetsContainer = ({ team }) => {
    const teamId = team?._id || team?.id;
    const logoAssets = useTeamAssets(teamId, TEAM_ASSET_TYPES.LOGO);
    const coverAssets = useTeamAssets(teamId, TEAM_ASSET_TYPES.COVER_PHOTO);
    const virtualBackgroundAssets = useTeamAssets(teamId, TEAM_ASSET_TYPES.VIRTUAL_BACKGROUND);
    const linkIconAssets = useTeamAssets(teamId, TEAM_ASSET_TYPES.LINK_ICON);
    const [virtualBackgroundOpen, setVirtualBackgroundOpen] = useState(false);
    const logoCardRef = useRef(null);
    const coverCardRef = useRef(null);
    const virtualBackgroundCardRef = useRef(null);
    const linkIconCardRef = useRef(null);
    const logoInputRef = useRef(null);
    const coverInputRef = useRef(null);
    const linkIconInputRef = useRef(null);

    const handleAssetUpload = useCallback(async (blob, assetType, storageSubType, fileName) => {
        if (!teamId) {
            console.error('Team ID is required to upload team assets');
            return;
        }

        try {
            const originalFileName = fileName || blob?.name || '';
            const storageId = `${teamId}-${Date.now()}`;
            const fileUrl = await uploadImageBlob(
                blob,
                null,
                'team',
                storageSubType,
                storageId
            );

            const createTarget = (() => {
                switch (assetType) {
                    case TEAM_ASSET_TYPES.LOGO:
                        return logoAssets;
                    case TEAM_ASSET_TYPES.COVER_PHOTO:
                        return coverAssets;
                    case TEAM_ASSET_TYPES.LINK_ICON:
                        return linkIconAssets;
                    default:
                        return null;
                }
            })();

            if (!createTarget) {
                throw new Error(`Unsupported team asset type: ${assetType}`);
            }
            await createTarget.createAsset({
                type: assetType,
                url: fileUrl,
                name: originalFileName,
                metadata: {
                    storageId,
                    storageSubType,
                    originalFileName
                }
            });
        } catch (error) {
            console.error('Error uploading team asset:', error);
        }
    }, [teamId, logoAssets, coverAssets, linkIconAssets]);

    const handleMenuUploadClick = useCallback((assetType) => {
        const refMap = {
            logo: logoInputRef,
            coverPhoto: coverInputRef,
            linkIcon: linkIconInputRef
        };
        const targetRef = refMap[assetType];
        if (targetRef?.current) {
            targetRef.current.click();
        }
    }, []);

    const handleAssetDelete = useCallback(async (assetId, assetType) => {
        const deleteTarget = (() => {
            switch (assetType) {
                case TEAM_ASSET_TYPES.LOGO:
                    return logoAssets;
                case TEAM_ASSET_TYPES.COVER_PHOTO:
                    return coverAssets;
                case TEAM_ASSET_TYPES.VIRTUAL_BACKGROUND:
                    return virtualBackgroundAssets;
                case TEAM_ASSET_TYPES.LINK_ICON:
                    return linkIconAssets;
                default:
                    return null;
            }
        })();
        if (!deleteTarget) {
            console.error(`Unsupported team asset type: ${assetType}`);
            return;
        }
        const assetList = deleteTarget.assets || [];
        const asset = assetList.find((item) => item._id === assetId);

        try {
            if (asset?.metadata?.storageId && asset?.metadata?.storageSubType) {
                await deleteImage('team', asset.metadata.storageSubType, asset.metadata.storageId);
            }
        } catch (error) {
            console.error('Error deleting asset from storage:', error);
        }

        await deleteTarget.deleteAsset(assetId);
    }, [logoAssets, coverAssets, virtualBackgroundAssets, linkIconAssets]);

    const assetCards = [
        {
            title: 'Logo',
            infoText: 'Upload your team logo.',
            buttonText: 'Select file',
            useFileInput: true,
            enableCrop: true,
            cropAspect: 1,
            cropShape: 'round',
            cropOutputShape: 'circle',
            cropMinZoom: 0.1,
            autoZoomOnLoad: true,
            initialZoom: 1,
            imagePreviewWidth: '6.5rem',
            imagePreviewAspectRatio: '1 / 1',
            imagePreviewRadius: '12px',
            onCroppedImage: (blob, fileName) => handleAssetUpload(blob, TEAM_ASSET_TYPES.LOGO, 'logo', fileName),
            assets: logoAssets.assets,
            onDeleteAsset: (assetId) => handleAssetDelete(assetId, TEAM_ASSET_TYPES.LOGO),
            ref: logoCardRef,
            fileInputRef: logoInputRef
        },
        {
            title: 'Cover Photos',
            infoText: 'Upload a default cover photo for your team.',
            buttonText: 'Select file',
            useFileInput: true,
            enableCrop: true,
            cropAspect: 2.6,
            cropShape: 'rect',
            cropOutputShape: 'rectangle',
            cropMinZoom: 0.05,
            autoZoomOnLoad: false,
            initialZoom: 1,
            imagePreviewWidth: '15.625rem',
            imagePreviewAspectRatio: '2.5 / 1',
            imagePreviewRadius: '8px',
            onCroppedImage: (blob, fileName) => handleAssetUpload(blob, TEAM_ASSET_TYPES.COVER_PHOTO, 'cover', fileName),
            assets: coverAssets.assets,
            onDeleteAsset: (assetId) => handleAssetDelete(assetId, TEAM_ASSET_TYPES.COVER_PHOTO),
            ref: coverCardRef,
            fileInputRef: coverInputRef
        },
        {
            title: 'Virtual Background',
            infoText: 'Create a new virtual background for your team.',
            buttonText: 'Create new',
            useFileInput: false,
            helperText: 'or',
            helperSubText: 'edit existing background',
            assets: virtualBackgroundAssets.assets,
            onDeleteAsset: (assetId) => handleAssetDelete(assetId, TEAM_ASSET_TYPES.VIRTUAL_BACKGROUND),
            onActionClick: () => setVirtualBackgroundOpen(true),
            imagePreviewWidth: '12rem',
            imagePreviewHeight: '7rem',
            imagePreviewAspectRatio: '2.5 / 1',
            imagePreviewRadius: '8px',
            savedAssetHeight: '15rem',
            ref: virtualBackgroundCardRef
        },
        {
            title: 'Link Icons',
            infoText: 'Upload icons for links used by your team.',
            buttonText: 'Select file',
            useFileInput: true,
            enableCrop: true,
            cropAspect: 1,
            cropShape: 'rect',
            cropOutputShape: 'rectangle',
            cropMinZoom: 0.1,
            autoZoomOnLoad: true,
            initialZoom: 1,
            imagePreviewWidth: '6.5rem',
            imagePreviewAspectRatio: '1 / 1',
            imagePreviewRadius: '4px',
            assets: linkIconAssets.assets,
            onDeleteAsset: (assetId) => handleAssetDelete(assetId, TEAM_ASSET_TYPES.LINK_ICON),
            onCroppedImage: (blob, fileName) => handleAssetUpload(blob, TEAM_ASSET_TYPES.LINK_ICON, 'icon', fileName),
            ref: linkIconCardRef,
            fileInputRef: linkIconInputRef
        }
    ];

    const handleSelectAssetType = useCallback((assetType) => {
        const refMap = {
            logo: logoCardRef,
            coverPhoto: coverCardRef,
            virtualBackground: virtualBackgroundCardRef,
            linkIcon: linkIconCardRef
        };
        const targetRef = refMap[assetType];
        if (targetRef?.current) {
            targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        if (assetType === 'virtualBackground') {
            setVirtualBackgroundOpen(true);
        } else {
            handleMenuUploadClick(assetType);
        }
    }, [handleMenuUploadClick]);

    return (
        <Box sx={{ padding: '1rem', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <TeamAssetsNavbar onSelectAssetType={handleSelectAssetType} />
            <Box sx={{ padding: '1.5rem 0 2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {assetCards.map((card) => (
                    <Box key={card.title} ref={card.ref}>
                        <TeamAssetsUploadCard
                            title={card.title}
                            infoText={card.infoText}
                            buttonText={card.buttonText}
                            useFileInput={card.useFileInput}
                            helperText={card.helperText}
                            helperSubText={card.helperSubText}
                            enableCrop={card.enableCrop}
                            cropAspect={card.cropAspect}
                            cropShape={card.cropShape}
                            cropOutputShape={card.cropOutputShape}
                            cropMinZoom={card.cropMinZoom}
                            onCroppedImage={card.onCroppedImage}
                            autoZoomOnLoad={card.autoZoomOnLoad}
                            initialZoom={card.initialZoom}
                            imagePreviewWidth={card.imagePreviewWidth}
                            imagePreviewHeight={card.imagePreviewHeight}
                            imagePreviewAspectRatio={card.imagePreviewAspectRatio}
                            imagePreviewRadius={card.imagePreviewRadius}
                            savedAssetWidth={card.savedAssetWidth}
                            savedAssetHeight={card.savedAssetHeight}
                            assets={card.assets}
                            onDeleteAsset={card.onDeleteAsset}
                            onActionClick={card.onActionClick}
                            fileInputRef={card.fileInputRef}
                        />
                        {card.title === 'Logo' && (
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(event) => handleMenuUploadClick && handleMenuUploadClick && event.target.files && card.onCroppedImage && card.onCroppedImage(event.target.files[0])}
                            />
                        )}
                    </Box>
                ))}
            </Box>
            <TeamVirtualBackgroundModal
                open={virtualBackgroundOpen}
                onClose={() => setVirtualBackgroundOpen(false)}
                teamId={teamId}
                teamAssets={virtualBackgroundAssets.assets}
                onSaved={() => virtualBackgroundAssets.refreshAssets()}
            />
        </Box>
    );
};

export default TeamAdminAssetsContainer;
